import * as angular from "angular";
import { ResultDataProvider } from "../../data-provider/result.dp";
import { FieldDataProvider } from "../../data-provider/field.dp";
import { DashboardDataProvider } from "../../data-provider/dashboard.dp";
import { PermissionCheck } from "../../shared/permission";
import {
    USER_PERFORMANCE_COLUMN_DEFS,
    USER_PERFORMANCE_COLUMN_TYPES,
    USER_PERFORMANCE_LEVEL_CONFIG,
    USER_PERFORMANCE_TARGET_CONFIG,
    UserPerformanceTableLevel,
} from "../../utils/constants/dashboard-widget.constants";
import { DashboardWidget } from "../../models/dashboard-widget.model";
import { DashboardWidgetFilter } from "../../models/dashboard-widget-filter.model";

let UserPerformanceDataTableComponent: any = {
    selector: "vcUserPerformanceDataTable",
    templateUrl:
        "/angular/components/result/user-performance-data-table.component.html",
    bindings: {
        widget: "=",
        filter: "=",
        mode: "=?",
    },
    controller: class UserPerformanceDataTableController {
        public navigationStack: UserPerformanceTableLevel[] = [];
        public loading: boolean = false;
        public firstLoadComplete: boolean = false;
        public orderLoading: boolean = false;
        public readonly targetConfig: Record<
            string,
            { label: string; description: string | null }
        > = USER_PERFORMANCE_TARGET_CONFIG;

        // Bindings
        public widget: any;
        public filter: any;

        public dataProvider: ResultDataProvider;
        public fieldDataProvider: FieldDataProvider;
        public dashboardDataProvider: DashboardDataProvider;
        public permissionProvider: PermissionCheck;
        private readonly isLeaderView: boolean;

        constructor(
            private $scope: any,
            private $filter: any,
            ResultDataProvider: ResultDataProvider,
            FieldDataProvider: FieldDataProvider,
            DashboardDataProvider: DashboardDataProvider,
            __app_type: string
        ) {
            this.dataProvider = ResultDataProvider;
            this.fieldDataProvider = FieldDataProvider;
            this.dashboardDataProvider = DashboardDataProvider;
            this.isLeaderView = __app_type === "LEADER";
        }

        private get initialLevel(): { type: string; title: string } {
            return this.isLeaderView
                ? { type: "divisions", title: "All Divisions" }
                : { type: "users", title: "All Users" };
        }

        $onInit(): void {
            this.loadLevel(this.initialLevel.type, this.initialLevel.title);

            this.$scope.$watch(
                () => this.widget,
                (
                    newValue: DashboardWidget,
                    oldValue: DashboardWidget
                ): void => {
                    if (oldValue && newValue !== oldValue) {
                        if (
                            oldValue.filter.order.direction ==
                                newValue.filter.order.direction &&
                            oldValue.filter.order.column ==
                                newValue.filter.order.column &&
                            this.dashboardDataProvider.widgetChangeRequiresReload(
                                newValue,
                                oldValue
                            )
                        ) {
                            this.navigationStack = [];
                            this.loadLevel(this.initialLevel.type, this.initialLevel.title);
                        }
                    }
                },
                true
            );

            this.$scope.$watch(
                () => this.filter,
                (
                    newValue: DashboardWidgetFilter,
                    oldValue: DashboardWidgetFilter
                ): void => {
                    if (
                        oldValue &&
                        newValue !== oldValue &&
                        newValue.selected.length == oldValue.selected.length &&
                        oldValue.order.direction == newValue.order.direction &&
                        oldValue.order.column == newValue.order.column
                    ) {
                        this.navigationStack = [];
                        this.loadLevel(this.initialLevel.type, this.initialLevel.title);
                    }
                },
                true
            );
        }

        get currentLevel(): UserPerformanceTableLevel {
            return this.navigationStack[this.navigationStack.length - 1];
        }

        /**
         * Returns only the columns defined for this level that are also selected in widget settings
         */
        getColumnsForLevel(type: string): any[] {
            const allowedIds: string[] =
                USER_PERFORMANCE_LEVEL_CONFIG[type].columns;
            const allCols: any[] = this.widget.style.tableOptions.columns || [];

            return allCols.filter(
                (col: any): boolean =>
                    col.selected && allowedIds.includes(col.id)
            );
        }

        loadLevel(type: string, title: string, parentId?: any) {
            if (this.loading) return;
            this.loading = true;
            let campaignId: number | null = null;

            if (type == "days") {
                let usersLevel: UserPerformanceTableLevel[] =
                    this.navigationStack.filter(
                        (item: UserPerformanceTableLevel) =>
                            item.type === "users"
                    );
                campaignId =
                    usersLevel.length > 0 ? usersLevel[0].parentId : null;
            }

            this.dataProvider
                .getPerformanceDataForLevel(
                    type,
                    parentId,
                    campaignId,
                    this.widget,
                    this.filter
                )
                .then((data: any[]) => {
                    this.navigationStack.push({
                        type: type,
                        title: title,
                        data: data,
                        parentId: parentId,
                        activeColumns: this.getColumnsForLevel(type),
                    });
                    this.sortData();
                    this.loading = false;
                    this.firstLoadComplete = true;
                });
        }

        expandRow(row: any): void {
            const config =
                USER_PERFORMANCE_LEVEL_CONFIG[this.currentLevel.type];
            if (config.next) {
                const title =
                    row.title || this.$filter("niceDateNoYear")(row.date);
                this.loadLevel(config.next, title, row.id);
            }
        }

        collapseRow(): void {
            if (this.navigationStack.length > 1) {
                this.navigationStack.pop();
            }
        }

        jumpToLevel(index: number): void {
            this.navigationStack = this.navigationStack.slice(0, index + 1);
        }

        getColConfig(colId: string): any {
            return (
                USER_PERFORMANCE_COLUMN_DEFS[colId] ||
                USER_PERFORMANCE_COLUMN_TYPES.DEFAULT
            );
        }

        public getPerformanceFigure(
            row: any,
            colId: string,
            type: string
        ): number {
            const prefix: string = colId.replace("_performance", "");

            if (type == "low")
                return Math.round(row[prefix + "_low_performing_percent"]);
            else if (type == "high")
                return Math.round(row[prefix + "_high_performing_percent"]);
            else return null;
        }

        public getCellValue(row: any, colId: string): string {
            const config =
                USER_PERFORMANCE_COLUMN_DEFS[colId] ||
                USER_PERFORMANCE_COLUMN_TYPES.DEFAULT;
            let formatted: string;

            if (config == USER_PERFORMANCE_COLUMN_TYPES.NUMBER_AND_PERCENT) {
                const percentValue = row[colId + "_percent"];
                formatted =
                    this.$filter("number")(percentValue, config.precision) +
                    "%";
            } else {
                const value = row[colId];

                if (value === null || value === undefined)
                    return config.fallback || "";

                formatted = value;

                // Apply Angular Filters
                if (config.filter) {
                    formatted = this.$filter(config.filter)(value);
                } else if (typeof config.precision === "number") {
                    formatted = this.$filter("number")(value, config.precision);
                }

                // Apply Suffix
                if (config.suffix) formatted += config.suffix;
            }

            return formatted;
        }

        setOrderBy(columnName: any): void {
            if (this.filter.order.column == columnName) {
                this.filter.order.direction =
                    this.filter.order.direction == "desc" ? "asc" : "desc";
            } else {
                this.filter.order.column = columnName;

                this.filter.order.direction =
                    columnName == "title" ? "asc" : "desc";
            }
            this.widget.filter.order = this.filter.order;
            this.dashboardDataProvider.updateWidget(this.widget);
            this.sortData();
        }

        isActiveSortColumn(column: string): boolean {
            if (this.currentLevel)
                return (
                    column == this.filter.order.column ||
                    column ==
                        this.convertColumnNameForSort(
                            this.filter.order.column,
                            this.currentLevel.type
                        )
                );
            return false;
        }

        convertColumnNameForSort(column: string, levelType: string): string {
            const isAggregate =
                levelType === "divisions" || levelType === "campaigns";

            const columnMap: Record<string, [string, string]> = {
                hourly_performance: [
                    "hourly_high_performing_percent",
                    "avg_canvasses_per_hour",
                ],
                day_performance: [
                    "day_high_performing_percent",
                    "avg_canvasses_per_day",
                ],
                shift_duration_performance: [
                    "shift_duration_high_performing_percent",
                    "avg_shift_hours",
                ],
                avg_shift_hours: [
                    "shift_duration_high_performing_percent",
                    "avg_shift_hours",
                ],
                leads_generated_performance: [
                    "leads_generated_high_performing_count",
                    "leads_generated_per_day",
                ],
                leads_generated_per_day: [
                    "leads_generated_high_performing_count",
                    "leads_generated_per_day",
                ],
                days_active_performance: [
                    "days_active_duration_high_performing_percent",
                    "days_active",
                ],
                days_active: [
                    "days_active_duration_high_performing_percent",
                    "days_active",
                ],
            };

            const mapping = columnMap[column];
            return mapping ? mapping[isAggregate ? 0 : 1] : column;
        }

        private isDateString(value: string): boolean {
            // Matches "Wed - 28 Jan" format
            return /^[A-Za-z]{3}\s*-\s*\d{1,2}\s+[A-Za-z]{3}$/.test(value);
        }

        private parseDate(dateStr: string): Date | null {
            // Parse "Wed - 28 Jan" format
            const match = dateStr.match(/(\d{1,2})\s+([A-Za-z]{3})/);
            if (!match) return null;

            const day = parseInt(match[1], 10);
            const monthStr = match[2];
            const months: Record<string, number> = {
                Jan: 0,
                Feb: 1,
                Mar: 2,
                Apr: 3,
                May: 4,
                Jun: 5,
                Jul: 6,
                Aug: 7,
                Sep: 8,
                Oct: 9,
                Nov: 10,
                Dec: 11,
            };

            const month = months[monthStr];
            if (month === undefined) return null;

            // Assume current year, or adjust based on your data
            const year = new Date().getFullYear();
            return new Date(year, month, day);
        }

        sortData(): void {
            let direction = this.filter.order.direction;

            angular.forEach(
                this.navigationStack,
                (level: UserPerformanceTableLevel) => {
                    let column = this.convertColumnNameForSort(
                        this.filter.order.column,
                        level.type
                    );

                    level.data.sort((a, b) => {
                        let valA = a[column];
                        let valB = b[column];

                        if (column === "title" && this.isDateString(valA)) {
                            const dateA = this.parseDate(valA);
                            const dateB = this.parseDate(valB);

                            if (dateA && dateB) {
                                return direction === "asc"
                                    ? dateA.getTime() - dateB.getTime()
                                    : dateB.getTime() - dateA.getTime();
                            }
                        }

                        // Convert numeric strings to numbers
                        if (
                            !isNaN(parseFloat(valA)) &&
                            !isNaN(parseFloat(valB))
                        ) {
                            valA = parseFloat(valA);
                            valB = parseFloat(valB);
                        }

                        return direction === "asc"
                            ? valA < valB
                                ? -1
                                : valA > valB
                                ? 1
                                : 0
                            : valA > valB
                            ? -1
                            : valA < valB
                            ? 1
                            : 0;
                    });
                }
            );
        }

        public getCellIcon(colId: string): string {
            return USER_PERFORMANCE_COLUMN_DEFS[colId]?.icon;
        }

        public getPerformanceClass(row: any, colId: string): string {
            const val = row[colId] ?? row[colId + "_count"];

            if (row["avg_canvasses_per_day"] == 0) {
                return "dimmed-zero";
            }
            // We specifically target the Count and Percent columns for performance
            if (colId.includes("leads_generated_per_day"))
                return val > this.widget.filter.target.leads_generated
                    ? "perf-badge perf-high"
                    : "perf-badge perf-low";
            else if (colId.includes("low_performing"))
                return val > 0 ? "perf-badge perf-low" : "";
            else if (colId.includes("high_performing"))
                return val > 0 ? "perf-badge perf-high" : "";
            else if (colId == "avg_canvasses_per_hour") {
                return val > this.widget.filter.target.hourly
                    ? "perf-badge perf-high"
                    : "perf-badge perf-low";
            } else if (colId == "avg_canvasses_per_day") {
                if (val == 0) {
                    return "dimmed-zero";
                }
                return val > this.widget.filter.target.daily
                    ? "perf-badge perf-high"
                    : "perf-badge perf-low";
            } else if (colId == "days_active") {
                if (val == 0) {
                    return "dimmed-zero";
                }
                return val > this.widget.filter.target.days_active
                    ? "perf-badge perf-high"
                    : "perf-badge perf-low";
            } else if (colId == "avg_shift_hours") {
                if (
                    (this.currentLevel.type == "users" ||
                        this.currentLevel.type == "days") &&
                    val == 0
                ) {
                    return "dimmed-zero";
                }
                return val > this.widget.filter.target.shift_duration
                    ? "perf-badge perf-high"
                    : "perf-badge perf-low";
            }

            return "";
        }

        // Optional helper to dim zero values
        public getValClass(val: any): string {
            return !val || val == 0 || val === "0.0" || val == "N/A"
                ? "dimmed-zero"
                : "";
        }

        // Logic to hide columns that don't make sense at this level
        public shouldShowColumn(colId: string): boolean {
            const type = this.currentLevel?.type;
            if (
                type === "users" &&
                (colId === "total_users" || colId === "active_users")
            ) {
                return false;
            }
            return true;
        }
    },
};

angular
    .module("result")
    .component(
        UserPerformanceDataTableComponent.selector,
        UserPerformanceDataTableComponent
    );
